/**
 * navigationConfig — declarative tree-shape sidebar for qfinpricer-portal.
 *
 * Comprehensive coverage of every route in router.tsx — every codegen'd CRUD
 * page + every custom analytics page. Audited against router.tsx on 2026-05-19.
 *
 * Spec: 2026-05-19-qfinpricer-portal-skin-qyas.md (Day 1, refined 2026-05-19)
 */

import React from 'react';
import * as LucideIcons from 'lucide-react';
import { navigationConfig as generatedNavigationConfig } from '../../registry/portalRegistry';
import { SECTION_ALIASES } from '../../theme/moduleColors';
import {
  Activity,
  Banknote,
  BarChart3,
  Bell,
  Bookmark,
  Bot,
  BookMarked,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  Cpu,
  Database,
  Droplet,
  FileText,
  Gauge,
  Hammer,
  LandPlot,
  Landmark,
  Layers,
  LineChart,
  Network,
  PieChart,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Workflow,
} from 'lucide-react';

export interface NavigationItem {
  label: string;
  path: string;
  icon?: React.ReactElement;
  children?: NavigationItem[];
}

export interface NavigationSection {
  id: string;
  label: string;
  icon: React.ReactElement;
  children: NavigationItem[];
}

const I = (n: number = 18) => n;
const Ico = {
  bookmark: <Bookmark size={I()} />,
  trending: <TrendingUp size={I()} />,
  briefcase: <Briefcase size={I()} />,
  sparkles: <Sparkles size={I()} />,
  pie: <PieChart size={I()} />,
  landmark: <Landmark size={I()} />,
  bot: <Bot size={I()} />,
  bookmarked: <BookMarked size={I()} />,
  bell: <Bell size={I()} />,
  cpu: <Cpu size={I()} />,
  barChart: <BarChart3 size={I()} />,
  droplet: <Droplet size={I()} />,
  lineChart: <LineChart size={I()} />,
  hammer: <Hammer size={I()} />,
  layers: <Layers size={I()} />,
  workflow: <Workflow size={I()} />,
  calendar: <Calendar size={I()} />,
  database: <Database size={I()} />,
  network: <Network size={I()} />,
  fileText: <FileText size={I()} />,
  calculator: <Calculator size={I()} />,
  banknote: <Banknote size={I()} />,
  building: <Building2 size={I()} />,
  shieldCheck: <ShieldCheck size={I()} />,
  settings: <Settings size={I()} />,
  gauge: <Gauge size={I()} />,
  target: <Target size={I()} />,
  activity: <Activity size={I()} />,
  landPlot: <LandPlot size={I()} />,
};

// ─────────────────────────────────────────────────────────────────────
// Reference Data — 18 admin entities
// ─────────────────────────────────────────────────────────────────────

const referenceDataSection: NavigationSection = {
  id: 'reference-data',
  label: 'Reference Data',
  icon: Ico.bookmark,
  children: [
    {
      label: 'Calendars & Holidays',
      path: '/reference-data/calendars',
      icon: Ico.calendar,
      children: [
        { label: 'Calendars', path: '/reference-data/admin/calendars' },
        { label: 'Holidays', path: '/reference-data/admin/holidays' },
      ],
    },
    {
      label: 'Currencies',
      path: '/reference-data/currencies',
      icon: Ico.banknote,
      children: [
        { label: 'Currencies', path: '/reference-data/admin/currencies' },
        { label: 'Currency Pairs', path: '/reference-data/admin/currency-pairs' },
      ],
    },
    {
      label: 'Conventions',
      path: '/reference-data/conventions',
      icon: Ico.settings,
      children: [
        { label: 'Day-Count Conventions', path: '/reference-data/admin/day-count-conventions' },
        { label: 'Business-Day Conventions', path: '/reference-data/admin/business-day-conventions' },
        { label: 'Frequencies', path: '/reference-data/admin/frequencies' },
        { label: 'Compoundings', path: '/reference-data/admin/compoundings' },
        { label: 'Date Generation Rules', path: '/reference-data/admin/date-generation-rules' },
        { label: 'Rate Averaging Types', path: '/reference-data/admin/rate-averaging-types' },
        { label: 'CPI Interpolation Types', path: '/reference-data/admin/cpi-interpolation-types' },
      ],
    },
    {
      label: 'Indexes',
      path: '/reference-data/indexes',
      icon: Ico.trending,
      children: [
        { label: 'Interest Rate Indexes', path: '/reference-data/admin/interest-rate-indexes' },
        { label: 'Inflation Indexes', path: '/reference-data/admin/inflation-indexes' },
      ],
    },
    {
      label: 'Instrument Types',
      path: '/reference-data/types',
      icon: Ico.layers,
      children: [
        { label: 'Instrument Types', path: '/reference-data/admin/instrument-types' },
        { label: 'Exercise Types', path: '/reference-data/admin/exercise-types' },
        { label: 'Option Types', path: '/reference-data/admin/option-types' },
        { label: 'Volatility Types', path: '/reference-data/admin/volatility-types' },
        { label: 'Duration Types', path: '/reference-data/admin/duration-types' },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────
// Market Data — 3 custom analytics + 16 admin entities
// ─────────────────────────────────────────────────────────────────────

const marketDataSection: NavigationSection = {
  id: 'market-data',
  label: 'Market Data',
  icon: Ico.trending,
  children: [
    {
      label: 'Analytics',
      path: '/market-data/analytics',
      icon: Ico.barChart,
      children: [
        { label: 'Curve Workbench', path: '/market-data/curves' },
      ],
    },
    {
      label: 'Quotes & Spots',
      path: '/market-data/quotes',
      icon: Ico.database,
      children: [
        { label: 'Market Quotes', path: '/market-data/admin/market-quotes' },
        { label: 'Fixings', path: '/market-data/admin/fixings' },
        { label: 'FX Spots', path: '/market-data/admin/fx-spots' },
        { label: 'Market Data Sets', path: '/market-data/admin/market-data-sets' },
      ],
    },
    {
      label: 'Curves Admin',
      path: '/market-data/curves-admin',
      icon: Ico.lineChart,
      children: [
        { label: 'Curve Definitions', path: '/market-data/admin/curve-definitions' },
        { label: 'Curve Snapshots', path: '/market-data/admin/curve-snapshots' },
        { label: 'Curve Points (read-only)', path: '/market-data/admin/curve-points' },
        { label: 'Curve Instruments', path: '/market-data/admin/curve-instruments' },
      ],
    },
    {
      label: 'Volatility Admin',
      path: '/market-data/vol-admin',
      icon: Ico.gauge,
      children: [
        { label: 'Vol Surfaces', path: '/market-data/admin/vol-surfaces' },
        { label: 'Vol Surface Snapshots', path: '/market-data/admin/vol-surface-snapshots' },
        { label: 'Vol Surface Points', path: '/market-data/admin/vol-surface-points' },
        { label: 'Vol Surface Quotes', path: '/market-data/admin/vol-surface-quotes' },
      ],
    },
    {
      label: 'Credit Admin',
      path: '/market-data/credit-admin',
      icon: Ico.shieldCheck,
      children: [
        { label: 'Credit Spread Curves', path: '/market-data/admin/credit-spread-curves' },
        { label: 'Credit Spread Points', path: '/market-data/admin/credit-spread-points' },
        { label: 'Credit Spread Snapshots', path: '/market-data/admin/credit-spread-snapshots' },
        { label: 'Credit Curve Points (read-only)', path: '/market-data/admin/credit-curve-points' },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────
// Instruments — pricer/options-lab + booking + admin + structured products
// ─────────────────────────────────────────────────────────────────────

const instrumentsSection: NavigationSection = {
  id: 'instruments',
  label: 'Instruments',
  icon: Ico.briefcase,
  children: [
    // Trading Desk production pass (2026-07-07): Desk is the /instruments
    // landing; Workbench / Blotter / Approval Inbox promoted alongside it.
    // The four per-type groups (Swaps / Bonds / Options & Swaptions /
    // FRAs & CDS) were REMOVED — every link in them was a redirect into the
    // workbench; their intent lives in the Desk quick actions. Legacy URLs
    // keep working (router redirects untouched).
    {
      label: 'Trading Workbench',
      path: '/instruments/workbench-group',
      icon: Ico.workflow,
      children: [
        { label: 'Desk', path: '/instruments' },
        { label: 'Workbench', path: '/instruments/workbench' },
        { label: 'Blotter', path: '/instruments/blotter' },
        { label: 'Approval Inbox', path: '/instruments/approvals' },
        {
          // The pre-workbench booking generation, kept live under -legacy
          // routes. Grouped here (same recursive third-level pattern as the
          // SP Admin node); the group's own path lands on a real page.
          label: 'Legacy',
          path: '/instruments/booking/swap-legacy',
          icon: Ico.database,
          children: [
            { label: 'Book Swap', path: '/instruments/booking/swap-legacy' },
            { label: 'Swaps', path: '/instruments/swaps-legacy' },
            { label: 'Book Bond', path: '/instruments/booking/bond-legacy' },
            { label: 'Bonds', path: '/instruments/bonds-legacy' },
            { label: 'Book FRA', path: '/instruments/booking/fra-legacy' },
            { label: 'FRAs', path: '/instruments/fras-legacy' },
            { label: 'Book Cap/Floor', path: '/instruments/booking/capfloor-legacy' },
            { label: 'Cap/Floors', path: '/instruments/capfloors-legacy' },
            { label: 'Book Option', path: '/instruments/booking/option-legacy' },
            { label: 'Options', path: '/instruments/options-book-legacy' },
            { label: 'Book Swaption', path: '/instruments/booking/swaption-legacy' },
            { label: 'Swaptions', path: '/instruments/swaptions-legacy' },
            { label: 'Book CDS', path: '/instruments/booking/cds-legacy' },
            { label: 'CDS', path: '/instruments/cdss-legacy' },
          ],
        },
      ],
    },
    {
      label: 'Pricing',
      path: '/instruments/pricing',
      icon: Ico.calculator,
      children: [
        { label: 'Instrument Pricer', path: '/instruments/pricer' },
        { label: 'Options Lab', path: '/instruments/options-lab' },
      ],
    },
    {
      // SP Desk P0 (spec 2026-07-07-sp-desk-frontend): Desk · Blotter · Book · Structure ·
      // Admin ▸. The ~28 generated CRUD pages are DEMOTED under a collapsed 'Admin'
      // sub-group (Navigation.tsx renders items recursively, so a third level works;
      // the Admin node's own path is a real page so the SectionSubTabs sub-menu — which
      // navigates on click — still lands somewhere honest).
      label: 'Structured Products',
      path: '/instruments/structured-products-group',
      icon: Ico.layers,
      children: [
        { label: 'Desk', path: '/instruments/structured-products' },
        { label: 'Blotter', path: '/instruments/structured-products/blotter' },
        { label: 'RFQ / Drafts', path: '/instruments/structured-products/rfq' },
        { label: 'Book Structured Product', path: '/instruments/structured-products/booking' },
        { label: 'Structure', path: '/instruments/structured-products/workbench' },
        { label: 'Pricing Lab', path: '/instruments/structured-products/pricing-lab' },
        { label: 'Approvals & EOD', path: '/instruments/structured-products/approvals' },
        {
          label: 'Admin',
          path: '/instruments/admin/structured-products',
          icon: Ico.database,
          children: [
            { label: 'SP Book (classic list)', path: '/instruments/structured-products/list' },
            { label: 'Structured Products (Admin)', path: '/instruments/admin/structured-products' },
            { label: 'Product Templates', path: '/instruments/admin/product-templates' },
            { label: 'FX Structured', path: '/instruments/admin/fx-structured' },
            { label: 'Equity Structured', path: '/instruments/admin/equity-structured' },
            { label: 'IRD Structured', path: '/instruments/admin/ird-structured' },
            { label: 'Commodity Linked', path: '/instruments/admin/commodity-linked' },
            { label: 'Credit Linked', path: '/instruments/admin/credit-linked' },
            { label: 'Barriers', path: '/instruments/admin/barriers' },
            { label: 'Coupon Schedules', path: '/instruments/admin/coupon-schedules' },
            { label: 'Approvals', path: '/instruments/admin/approvals' },
            { label: 'Lifecycle Events', path: '/instruments/admin/lifecycle-events' },
            { label: 'Termsheets', path: '/instruments/admin/termsheets' },
            { label: 'Underlyings', path: '/instruments/admin/underlyings' },
            { label: 'Settlements', path: '/instruments/admin/settlements' },
            { label: 'Cashflows', path: '/instruments/admin/cashflows' },
            { label: 'Observation Schedules', path: '/instruments/admin/observation-schedules' },
            { label: 'Product Links', path: '/instruments/admin/product-links' },
            { label: 'Accumulator Configs', path: '/instruments/admin/accumulator-configs' },
            { label: 'Range Accrual Configs', path: '/instruments/admin/range-accrual-configs' },
            { label: 'TARF Configs', path: '/instruments/admin/tarf-configs' },
            { label: 'Greeks', path: '/instruments/admin/greekses' },
            { label: 'Pricing Results (SP)', path: '/instruments/admin/pricing-results' },
            { label: 'Pricing Request Logs', path: '/instruments/admin/pricing-request-logs' },
            { label: 'Scenarios (SP)', path: '/instruments/admin/scenarios' },
            { label: 'Scenario Results (SP)', path: '/instruments/admin/scenario-results' },
            { label: 'Market Data Snapshots', path: '/instruments/admin/market-data-snapshots' },
            { label: 'Fixings (SP)', path: '/instruments/admin/fixings' },
            { label: 'Audit Trails', path: '/instruments/admin/audit-trails' },
          ],
        },
      ],
    },
    {
      label: 'Trade Admin',
      path: '/instruments/admin-group',
      icon: Ico.database,
      children: [
        { label: 'Instruments', path: '/instruments/admin/instruments' },
        { label: 'Bonds', path: '/instruments/admin/bonds' },
        { label: 'Swaps', path: '/instruments/admin/swaps' },
        { label: 'Swap Legs', path: '/instruments/admin/swap-legs' },
        { label: 'Options', path: '/instruments/admin/options' },
        { label: 'Exotic Options', path: '/instruments/admin/exotic-options' },
        { label: 'Cap-Floors', path: '/instruments/admin/cap-floors' },
        { label: 'Swaptions', path: '/instruments/admin/swaptions' },
        { label: 'FRAs', path: '/instruments/admin/fras' },
        { label: 'CDS', path: '/instruments/admin/cds' },
        { label: 'FX Instruments', path: '/instruments/admin/fx-instruments' },
        { label: 'Trades', path: '/instruments/admin/trades' },
        { label: 'Portfolios', path: '/instruments/admin/portfolios' },
        { label: 'Portfolio Positions', path: '/instruments/admin/portfolio-positions' },
        { label: 'Instrument Cashflows', path: '/instruments/admin/instrument-cashflows' },
        { label: 'Schedules', path: '/instruments/admin/schedules' },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────
// Risk Analytics — 3 custom analytics + 12 admin entities
// ─────────────────────────────────────────────────────────────────────

const riskSection: NavigationSection = {
  id: 'risk',
  label: 'Risk Analytics',
  icon: Ico.sparkles,
  children: [
    {
      label: 'Analytics',
      path: '/risk/analytics',
      icon: Ico.gauge,
      children: [
        { label: 'Risk Dashboard', path: '/risk/dashboard' },
        { label: 'Scenario Analyzer', path: '/risk/scenario-analyzer' },
        { label: 'Calibration Console', path: '/risk/calibration-console' },
        { label: 'Sensitivity Grid', path: '/risk/sensitivity-run' },
        { label: 'Risk Pack', path: '/risk/reports' },
      ],
    },
    {
      label: 'Pricing',
      path: '/risk/pricing',
      icon: Ico.calculator,
      children: [
        { label: 'Pricing Requests', path: '/risk/admin/pricing-requests' },
        { label: 'Pricing Results', path: '/risk/admin/pricing-results' },
        { label: 'Pricing Configurations', path: '/risk/admin/pricing-configurations' },
        { label: 'Greeks Snapshots', path: '/risk/admin/greeks-snapshots' },
      ],
    },
    {
      label: 'Scenarios & Stress',
      path: '/risk/scenarios',
      icon: Ico.activity,
      children: [
        { label: 'Stress Run', path: '/risk/stress-run' },
        { label: 'Scenarios', path: '/risk/admin/scenarios' },
        { label: 'Scenario Results', path: '/risk/admin/scenario-results' },
        { label: 'Stress Tests', path: '/risk/admin/stress-tests' },
        { label: 'Stress Test Results', path: '/risk/admin/stress-test-results' },
      ],
    },
    {
      label: 'Models',
      path: '/risk/models',
      icon: Ico.network,
      children: [
        { label: 'Model Definitions', path: '/risk/admin/model-definitions' },
        { label: 'Calibration Results', path: '/risk/admin/calibration-results' },
        { label: 'Sensitivity Grids', path: '/risk/admin/sensitivity-grids' },
        { label: 'Risk Metrics', path: '/risk/admin/risk-metrics' },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────
// Portfolio
// ─────────────────────────────────────────────────────────────────────

const portfolioSection: NavigationSection = {
  id: 'portfolio',
  label: 'Portfolio',
  icon: Ico.pie,
  children: [
    { label: 'Portfolio Dashboard', path: '/portfolio/dashboard', icon: Ico.gauge },
    { label: 'Portfolios', path: '/portfolio/admin/portfolios', icon: Ico.briefcase },
    { label: 'Positions', path: '/portfolio/admin/positions', icon: Ico.layers },
    { label: 'Trades', path: '/portfolio/admin/trades', icon: Ico.database },
  ],
};

// ─────────────────────────────────────────────────────────────────────
// BSM & Risk Mngt (alm-service) — 4 analytics + 17 admin entities
// ─────────────────────────────────────────────────────────────────────

const almSection: NavigationSection = {
  id: 'alm',
  label: 'BSM & Risk Mngt',
  icon: Ico.landmark,
  children: [
    // R2b task 2.1: the 'Run' group is the topmost group in this section --
    // all 6 run-lane pages (1.2-1.6) live here, above Analytics/admin CRUD.
    {
      label: 'Run',
      path: '/alm/run',
      icon: Ico.activity,
      children: [
        { label: 'EVE Analysis', path: '/alm/eve-analysis' },
        { label: 'NII Run', path: '/alm/nii-run' },
        // Route under /bsm (not /alm/stress-run, not /risk/stress-run --
        // that's the unrelated risk-service lane). See RUN-CHECKLIST.md
        // naming-collision note.
        { label: 'ALM Stress Run', path: '/bsm/stress-run' },
        { label: 'FTP Compute', path: '/alm/ftp-compute' },
        { label: 'Reconciliation Run', path: '/alm/reconciliation-run' },
        { label: 'Reports', path: '/alm/reports' },
      ],
    },
    {
      label: 'Analytics',
      path: '/alm/analytics',
      icon: Ico.barChart,
      children: [
        { label: 'LCR Dashboard', path: '/alm/lcr-dashboard' },
        { label: 'NSFR Dashboard', path: '/alm/nsfr-dashboard' },
        { label: 'Gap Analysis', path: '/alm/gap-analysis' },
        { label: 'Position Upload', path: '/alm/position-upload' },
      ],
    },
    {
      label: 'Liquidity Data',
      path: '/alm/liquidity-data',
      icon: Ico.droplet,
      children: [
        { label: 'Liquidity Ratios', path: '/alm/admin/liquidity-ratio' },
        { label: 'LCR Runoff Rates', path: '/alm/admin/lcr-runoff-rate' },
        { label: 'Liquidity Cashflows', path: '/alm/admin/liquidity-cashflow' },
      ],
    },
    {
      label: 'IRRBB Data',
      path: '/alm/irrbb-data',
      icon: Ico.lineChart,
      children: [
        { label: 'EVE Simulations', path: '/alm/admin/eve-simulation' },
        { label: 'NII Simulations', path: '/alm/admin/nii-simulation' },
        { label: 'Gap Records', path: '/alm/admin/gap-analysis' },
      ],
    },
    {
      label: 'Models & Scenarios',
      path: '/alm/models',
      icon: Ico.network,
      children: [
        { label: 'Behavioral Models', path: '/alm/admin/behavioral-model' },
        { label: 'Stress Scenarios', path: '/alm/admin/stress-scenario-alm' },
        { label: 'Monte Carlo', path: '/alm/admin/monte-carlo-simulation' },
      ],
    },
    {
      label: 'Operations',
      path: '/alm/operations',
      icon: Ico.workflow,
      children: [
        { label: 'BSM Positions', path: '/alm/admin/alm-position' },
        { label: 'FTP Rates', path: '/alm/admin/ftp-rate' },
        { label: 'FTP Allocations', path: '/alm/admin/ftp-allocation' },
        { label: 'Reconciliation', path: '/alm/admin/reconciliation-result' },
        { label: 'Audit Trail', path: '/alm/admin/audit-trail' },
        { label: 'Tolerance Rules', path: '/alm/admin/tolerance-rule' },
      ],
    },
    {
      label: 'Capital & RWA',
      path: '/alm/capital',
      icon: Ico.shieldCheck,
      children: [
        { label: 'Basel Capital', path: '/alm/admin/basel-capital-requirement' },
        { label: 'RWA Calculations', path: '/alm/admin/rwa-calculation' },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────
// System — operational surfaces (events, orchestrator)
// ─────────────────────────────────────────────────────────────────────

const systemSection: NavigationSection = {
  id: 'system',
  label: 'System',
  icon: Ico.cpu,
  children: [
    { label: 'Dashboard', path: '/dashboard', icon: Ico.gauge },
    { label: 'Events Monitor', path: '/events', icon: Ico.bell },
    { label: 'Orchestrator', path: '/orchestrator/dashboard', icon: Ico.bot },
  ],
};

// ─────────────────────────────────────────────────────────────────────
// Knowledge Wiki
// ─────────────────────────────────────────────────────────────────────

const wikiSection: NavigationSection = {
  id: 'wiki',
  label: 'Knowledge Wiki',
  icon: Ico.bookmarked,
  children: [
    { label: 'Browse', path: '/wiki', icon: Ico.fileText },
    { label: 'Ingest', path: '/wiki/ingest', icon: Ico.hammer },
    { label: 'Lint', path: '/wiki/lint', icon: Ico.shieldCheck },
  ],
};

// ─────────────────────────────────────────────────────────────────────
// Final tree
// ─────────────────────────────────────────────────────────────────────

const handSections: NavigationSection[] = [
  referenceDataSection,
  marketDataSection,
  instrumentsSection,
  riskSection,
  portfolioSection,
  almSection,
  wikiSection,
];

// ── Generated-module merge ─────────────────────────────────────────────────
// Sections for modules born from `codaic add service` come from the generated
// registry, so a new backend service appears in this sidebar without anyone
// editing this file. Hand sections above always win for the modules they cover;
// System stays last.
const iconFor = (name: string): React.ReactElement => {
  const C = (LucideIcons as Record<string, unknown>)[name] as
    | React.ComponentType<{ size?: number }>
    | undefined;
  const Icon = C ?? LucideIcons.LayoutGrid;
  return <Icon size={18} />;
};

// Aliased generated sections fold their children into the hand section that
// owns the concept ('instrument' -> 'instruments'): the curated groups keep the
// top spots, and the generated Entities tree (every CRUD page, including the
// DEC-067 trade extensions) lands beneath them. 'Overview' is skipped — the
// hand section already has a landing — and anything whose label or path the
// hand section already carries is skipped rather than duplicated.
for (const gen of generatedNavigationConfig) {
  const targetId = SECTION_ALIASES[gen.id];
  if (!targetId) continue;
  const target = handSections.find((h) => h.id === targetId);
  if (!target) continue;
  const ownedLabels = new Set(target.children.map((c) => c.label));
  const ownedPaths = new Set<string>();
  const walk = (items: NavigationItem[]) =>
    items.forEach((i) => { ownedPaths.add(i.path); if (i.children) walk(i.children); });
  walk(target.children);
  for (const child of gen.children as NavigationItem[]) {
    if (child.label === 'Overview') continue;
    if (ownedLabels.has(child.label) || ownedPaths.has(child.path)) continue;
    target.children.push(child);
  }
}

const generatedSections: NavigationSection[] = generatedNavigationConfig
  .filter(
    (s) =>
      s.id !== 'system' &&
      !(s.id in SECTION_ALIASES) &&
      !handSections.some((h) => h.id === s.id),
  )
  .map((s) => ({
    id: s.id,
    label: s.label,
    icon: iconFor(s.iconName),
    children: s.children as NavigationItem[],
  }));

export const navigationConfig: NavigationSection[] = [
  ...handSections,
  ...generatedSections,
  systemSection,
];

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

export interface FlatSearchEntry {
  label: string;
  path: string;
  sectionId: string;
  sectionLabel: string;
  breadcrumb: string;
}

export function buildSearchIndex(): FlatSearchEntry[] {
  const out: FlatSearchEntry[] = [];
  const walk = (
    items: NavigationItem[],
    section: NavigationSection,
    trail: string[],
  ) => {
    for (const item of items) {
      const crumb = [section.label, ...trail, item.label].join(' › ');
      out.push({
        label: item.label,
        path: item.path,
        sectionId: section.id,
        sectionLabel: section.label,
        breadcrumb: crumb,
      });
      if (item.children?.length) {
        walk(item.children, section, [...trail, item.label]);
      }
    }
  };
  for (const section of navigationConfig) {
    walk(section.children, section, []);
  }
  return out;
}

/** Find the navigation section that owns a given route path. */
export function findSectionForPath(path: string): NavigationSection | undefined {
  for (const section of navigationConfig) {
    const root = '/' + section.id;
    if (path === root || path.startsWith(root + '/')) return section;
  }
  for (const section of navigationConfig) {
    const matches = (items: NavigationItem[]): boolean => {
      for (const item of items) {
        if (path === item.path || path.startsWith(item.path + '/')) return true;
        if (item.children && matches(item.children)) return true;
      }
      return false;
    };
    if (matches(section.children)) return section;
  }
  return undefined;
}
